/**
 * Builds the one typed payload the Infrastructure flow submits, the summary shown on the Review step, and the few legacy
 * fields (region name, resource group, VNet, subnet, database) that the rest of the wizard and the Traverse pipeline
 * still read.
 */
import { CUSTOMER_CODE_LABEL } from '@/data/customerCode'
import { HYBRID_BENEFIT_LABEL, RDS_GOLDEN_IMAGE, SECURITY_TYPE, SECURITY_TYPE_LABEL } from '@/data/dedicatedServers'
import { environmentTypeOf } from '@/data/environmentTypes'
import { licenseFieldsFor } from '@/data/licenses'
import { linkVersionLabel } from '@/data/linkVersions'
import { hasProductLine } from '@/data/productLines'
import { PUBLIC_IP_KIND } from '@/data/publicIp'
import { SQL_INSTANCE_QUESTION, mainInstanceNote } from '@/data/sqlInstance'
import { componentsFor } from '@/data/traverseComponents'
import type {
  AddOnPayload,
  DataDisk,
  DbServerPayload,
  IisAddOnPayload,
  ReviewEditTarget,
  InfrastructurePayload,
  LicensePayload,
  NetworkChoice,
  NetworkPayload,
  RdsServerPayload,
  SqlDiskGroup,
  SqlDiskGroupPayload,
  SqlInstancePayload,
  SqlStorageConfig,
  SqlStoragePayload,
  VmSizeCheck,
} from '@/types/infrastructure'
import type { AddOnData, IisAddOnData, InfraWizardData } from '@/stores/infrastructureWizard'
import { asksRdsVmCount, dbPublicIp, effectiveServerMode, rdsPublicIp, rdsVmCount } from '@/stores/infrastructureWizard'
import { paasPayloadValues } from '@/utils/infraPaasFields'
import { asksAnalyticsAddOns, asksLicense, dbNameStyle, resolveServerName, validateInfrastructure, type InfraContext } from '@/utils/infraValidation'
import { hasNamingRule } from '@/utils/naming'
import { agreedVmSizes, sizeOfTier, sizeText } from '@/utils/vmSizes'

const names = (network: NetworkChoice): NetworkPayload => ({ vnet: network.vnet?.name ?? '', subnet: network.subnet?.name ?? '' })
const withoutKey = (disk: DataDisk): Omit<DataDisk, 'key'> => ({
  lun: disk.lun,
  sizeGb: disk.sizeGb,
  sku: disk.sku,
  caching: disk.caching,
  driveLetter: disk.driveLetter.toUpperCase(),
})

const groupPayload = (group: SqlDiskGroup): SqlDiskGroupPayload => ({
  driveLocation: group.driveLocation.trim(),
  diskType: group.sku.diskType,
  sku: group.sku.id,
  diskSizeGb: group.sku.sizeGb,
  diskCount: group.count,
  iopsPerDisk: group.sku.maxIops,
  throughputMbpsPerDisk: group.sku.maxThroughputMbps,
})

/** The SQL server's storage as it is submitted: the sizes are spelled out, so the server needs no catalog to read it. */
export function sqlStoragePayload(config: SqlStorageConfig): SqlStoragePayload {
  return {
    vmSize: config.vm.vmSize,
    data: groupPayload(config.data),
    log: groupPayload(config.log),
    tempDb: config.tempDb.useLocalSsd ? { useLocalSsd: true } : { useLocalSsd: false, ...groupPayload(config.tempDb) },
    systemDatabasesOnDataDrive: config.systemDatabasesOnDataDrive,
  }
}

function addOnPayload(addOn: AddOnData): AddOnPayload | null {
  if (!addOn.enabled || !addOn.goldenImage) return null
  return { goldenImage: addOn.goldenImage, network: names(addOn.network) }
}

/** Customer Portal or a component as submitted: the IIS server, and nothing at all when the answer is No. */
function iisPayload(addOn: IisAddOnData): IisAddOnPayload | undefined {
  if (!addOn.enabled || !addOn.server) return undefined
  return { enabled: true, iisServerId: addOn.server.id, iisServer: addOn.server }
}

function rdsServerPayload(data: InfraWizardData): RdsServerPayload | null {
  const mode = effectiveServerMode(data.product, data.rdsServer.mode, 'rds')
  if (mode === 'existing') return data.rdsServer.existing ? { mode: 'existing', server: data.rdsServer.existing } : null
  if (mode !== 'dedicated') return null
  const count = rdsVmCount(data)
  // Every name of the series is checked (none may be too long), and the first is the server's name.
  const name = hasNamingRule(data.product) ? resolveServerName(data, 'rds', 'host', count) : null
  if (name && !name.ok) return null
  return {
    mode: 'dedicated',
    // Only the products that use the pool tiers are asked how many RDS VMs: the others send none.
    ...(asksRdsVmCount(data) && { count }),
    ...(name?.ok && { serverName: name.name, nameNote: name.note }),
    image: { ...RDS_GOLDEN_IMAGE },
    securityType: SECURITY_TYPE,
    network: names(data.rdsServer.network),
    // One answer for the whole set: a public IP on every RDS VM, or on none. Always sent for a Dedicated server (No by default).
    publicIp: rdsPublicIp(data),
    dataDisks: data.rdsServer.disks.map(withoutKey),
  }
}

function dbServerPayload(data: InfraWizardData): DbServerPayload | null {
  const mode = effectiveServerMode(data.product, data.dbServer.mode, 'db')
  if (mode === 'existing') {
    const server = data.dbServer.existing
    if (!server) return null
    return { mode: 'existing', server: { name: server.name, resourceGroup: server.resourceGroup, image: server.image, vnet: server.vnet, subnet: server.subnet } }
  }
  if (mode !== 'dedicated') return null
  const name = resolveServerName(data, 'db', dbNameStyle(data.level))
  if (!name.ok) return null
  if (data.level === 'appCentral') {
    const { azureSql } = data.dbServer
    return { mode: 'dedicated', kind: 'azureSql', name: name.name, nameNote: name.note, network: names(azureSql.network), settings: paasPayloadValues(azureSql.paas) }
  }
  const { sqlVm } = data.dbServer
  if (!sqlVm.image || !sqlVm.storage) return null
  return {
    mode: 'dedicated',
    kind: 'sqlVm',
    serverName: name.name,
    nameNote: name.note,
    image: sqlVm.image,
    network: names(sqlVm.network),
    // Only a SQL server VM has the question (an Azure SQL server has no such field), so it is sent here and nowhere else.
    publicIp: dbPublicIp(data),
    storage: sqlStoragePayload(sqlVm.storage),
    authType: sqlVm.authType,
    adminUser: sqlVm.authType === 'windows' ? '' : sqlVm.adminUser,
    adminPassword: sqlVm.authType === 'windows' ? '' : sqlVm.adminPassword,
    collation: sqlVm.collation.trim(),
  }
}

/** The SQL instance answer as submitted. Only a Yes carries a name; No means the server's main instance. */
function sqlInstancePayload(data: InfraWizardData): SqlInstancePayload {
  return data.sqlInstance.required
    ? { required: true, instanceName: data.sqlInstance.instanceName.trim(), useMainInstance: false }
    : { required: false, useMainInstance: true }
}

/** The license fields of the environment's type only. None until every one is filled in: it is asked on the Application (ERP) step. */
function licensePayload(data: InfraWizardData): LicensePayload | undefined {
  if (!asksLicense(data.product)) return undefined
  const fields = licenseFieldsFor(environmentTypeOf(data.environment))
  if (fields.length === 0 || fields.some((field) => data.license[field.key].trim() === '')) return undefined
  return Object.fromEntries(fields.map((field) => [field.key, data.license[field.key].trim()])) as LicensePayload
}

/** The payload, or null while anything is still missing or invalid. */
export function buildInfrastructurePayload(data: InfraWizardData, context: InfraContext = {}): InfrastructurePayload | null {
  if (!data.product || !data.environment || !data.region || !data.resourceGroup || !data.level) return null
  if (!validateInfrastructure(data, context).valid) return null
  const rdsServer = rdsServerPayload(data)
  const dbServer = dbServerPayload(data)
  if (!rdsServer || !dbServer) return null
  const portal = iisPayload(data.customerPortal)
  const components = Object.fromEntries(
    componentsFor(data.product).flatMap((component) => {
      const chosen = iisPayload(data[component.key])
      return chosen ? [[component.key, chosen]] : []
    }),
  )
  const license = licensePayload(data)
  const vmSizes = agreedVmSizes(data)
  return {
    product: data.product,
    location: {
      environment: { id: data.environment.id, label: data.environment.label },
      region: { id: data.region.id, label: data.region.label },
      resourceGroup: { id: data.resourceGroup.id, name: data.resourceGroup.name },
    },
    customerNumber: data.customerNumber,
    level: data.level,
    rdsServer,
    dbServer,
    sqlInstance: sqlInstancePayload(data),
    ...(license && { license }),
    // Asked of Process Pro Global only; every other product leaves both keys out.
    ...(asksAnalyticsAddOns(data.product) && { advancedAnalytics: addOnPayload(data.advancedAnalytics), bartender: addOnPayload(data.bartender) }),
    // Asked of the product lines only. An answer of No leaves its key out.
    ...(hasProductLine(data.product) && {
      ...(portal && { customerPortal: portal }),
      ...components,
      ...(data.linkVersion && { linkVersion: data.linkVersion }),
    }),
    // The series of the same size the user agreed to build where the region cannot build the configured one. Left out when there is none.
    ...(Object.keys(vmSizes).length > 0 && { vmSizes }),
  }
}

// ─── What the rest of the wizard still reads ─────────────────────────────────

export interface LegacyInfraFields {
  /** The region's display name: what a deployment request carries as azure_region. */
  azureRegion: string
  resourceGroup: string
  vnet: string
  subnet: string
  /** Same shape as the Traverse database block the pipeline config has always carried. */
  database: { mode: 'existing' | 'new'; server_name: string; server_resource_group: string; instance_name: string } | undefined
}

/** The RDS server's network: the existing server's, or the one chosen for the dedicated server. */
function mainNetwork(data: InfraWizardData): NetworkPayload {
  const mode = effectiveServerMode(data.product, data.rdsServer.mode, 'rds')
  if (mode === 'existing') return { vnet: data.rdsServer.existing?.vnet ?? '', subnet: data.rdsServer.existing?.subnet ?? '' }
  if (mode === 'dedicated') return names(data.rdsServer.network)
  return { vnet: '', subnet: '' }
}

export function legacyFields(data: InfraWizardData): LegacyInfraFields {
  const network = mainNetwork(data)
  const instance = data.sqlInstance.required ? data.sqlInstance.instanceName.trim() : ''
  const mode = data.level ? effectiveServerMode(data.product, data.dbServer.mode, 'db') : null
  let database: LegacyInfraFields['database']
  if (mode === 'existing') {
    const server = data.dbServer.existing
    database = { mode: 'existing', server_name: server?.name ?? '', server_resource_group: server?.resourceGroup ?? '', instance_name: instance }
  } else if (mode === 'dedicated') {
    const resolved = resolveServerName(data, 'db', dbNameStyle(data.level))
    database = { mode: 'new', server_name: resolved.ok ? resolved.name : '', server_resource_group: data.resourceGroup?.name ?? '', instance_name: instance }
  }
  return {
    azureRegion: data.region?.label ?? '',
    resourceGroup: data.resourceGroup?.name ?? '',
    vnet: network.vnet,
    subnet: network.subnet,
    database,
  }
}

// ─── Review summary ──────────────────────────────────────────────────────────

export interface ReviewRowData {
  label: string
  value: string
  mono?: boolean
}

export interface ReviewSectionData {
  title: string
  /** Where "Edit" goes. Null for the product, which is chosen in the first step of the wizard. */
  editStep: ReviewEditTarget | null
  rows: ReviewRowData[]
}

const MASK = '••••••••'
const yes = (value: boolean) => (value ? 'Yes' : 'No')
const networkText = (network: NetworkPayload) => `${network.vnet} / ${network.subnet}`
const diskText = (disks: Omit<DataDisk, 'key'>[]) =>
  disks.length === 0 ? 'None' : disks.map((d) => `${d.driveLetter.toUpperCase()}: ${d.sizeGb} GB ${d.sku}, ${d.caching} caching, LUN ${d.lun}`).join('; ')

function rdsRows(rds: RdsServerPayload, size: VmSizeCheck | undefined): ReviewRowData[] {
  if (rds.mode === 'existing') {
    return [
      { label: 'RDS server', value: `${rds.server.name} (existing)`, mono: true },
      { label: 'RDS resource group', value: rds.server.resourceGroup, mono: true },
      { label: 'RDS network', value: `${rds.server.vnet} / ${rds.server.subnet}${rds.server.ip ? ` · ${rds.server.ip}` : ''}`, mono: true },
    ]
  }
  return [
    ...(rds.serverName
      ? [
          { label: 'RDS server', value: `${rds.serverName} (dedicated)`, mono: true },
          { label: 'RDS name derived', value: rds.nameNote ?? '' },
        ]
      : [{ label: 'RDS server', value: 'Dedicated' }]),
    ...(rds.count !== undefined ? [{ label: 'RDS VMs', value: String(rds.count) }] : []),
    ...(size ? [{ label: 'RDS VM size', value: sizeText(size), mono: true }] : []),
    { label: 'RDS image', value: rds.image.label },
    { label: 'RDS security type', value: SECURITY_TYPE_LABEL },
    { label: 'RDS licensing', value: HYBRID_BENEFIT_LABEL },
    { label: 'RDS network', value: networkText(rds.network), mono: true },
    { label: 'RDS public IP', value: rds.publicIp ? `Yes (${PUBLIC_IP_KIND}; one per RDS VM)` : 'No' },
    { label: 'RDS data disks', value: diskText(rds.dataDisks) },
  ]
}

const diskGroupText = (group: SqlDiskGroupPayload) =>
  `${group.driveLocation} · ${group.diskCount} × ${group.diskSizeGb} GiB ${group.diskType} (${group.sku}): ` +
  `${group.diskSizeGb * group.diskCount} GiB, ${group.iopsPerDisk * group.diskCount} IOPS, ${group.throughputMbpsPerDisk * group.diskCount} MB/s`

function sqlStorageRows(storage: SqlStoragePayload, size: VmSizeCheck | undefined): ReviewRowData[] {
  return [
    { label: 'SQL VM size', value: size ? sizeText(size) : storage.vmSize, mono: true },
    { label: 'SQL data storage', value: diskGroupText(storage.data), mono: true },
    { label: 'SQL log storage', value: diskGroupText(storage.log), mono: true },
    { label: 'SQL tempdb storage', value: storage.tempDb.useLocalSsd ? 'Local SSD (the VM’s temporary disk)' : diskGroupText(storage.tempDb), mono: !storage.tempDb.useLocalSsd },
    { label: 'System databases', value: storage.systemDatabasesOnDataDrive ? `Moved to the data storage (${storage.data.driveLocation})` : 'Default location' },
  ]
}

/** The DB server's name, whichever kind it is. */
const dbServerName = (db: DbServerPayload): string => (db.mode === 'existing' ? db.server.name : db.kind === 'azureSql' ? db.name : db.serverName)

function dbRows(db: DbServerPayload, size: VmSizeCheck | undefined): ReviewRowData[] {
  if (db.mode === 'existing') {
    return [
      { label: 'DB server', value: `${db.server.name} (existing)`, mono: true },
      { label: 'DB resource group', value: db.server.resourceGroup, mono: true },
      { label: 'DB network', value: `${db.server.vnet} / ${db.server.subnet}`, mono: true },
    ]
  }
  if (db.kind === 'azureSql') {
    const settings = Object.entries(db.settings)
      .filter(([, value]) => value !== '')
      .map(([key, value]) => `${key}: ${key === 'adminPassword' ? MASK : value}`)
      .join('; ')
    return [
      { label: 'Azure SQL server (PaaS)', value: `${db.name} (dedicated)`, mono: true },
      { label: 'Name derived', value: db.nameNote },
      { label: 'Azure SQL network', value: networkText(db.network), mono: true },
      { label: 'PaaS settings', value: settings },
    ]
  }
  return [
    { label: 'DB server', value: `${db.serverName} (dedicated)`, mono: true },
    { label: 'Name derived', value: db.nameNote },
    { label: 'SQL image', value: db.image.name },
    { label: 'DB network', value: networkText(db.network), mono: true },
    { label: 'DB public IP', value: db.publicIp ? `Yes (${PUBLIC_IP_KIND})` : 'No' },
    ...sqlStorageRows(db.storage, size),
    { label: 'SQL authentication', value: db.authType },
    ...(db.authType === 'windows'
      ? []
      : [
          { label: 'SQL admin user', value: db.adminUser, mono: true },
          { label: 'SQL admin password', value: MASK },
        ]),
    { label: 'SQL collation', value: db.collation, mono: true },
  ]
}

function sqlInstanceRows(answer: SqlInstancePayload, db: DbServerPayload): ReviewRowData[] {
  return [
    { label: SQL_INSTANCE_QUESTION, value: yes(answer.required) },
    answer.required
      ? { label: 'SQL instance', value: answer.instanceName, mono: true }
      : { label: 'SQL instance', value: mainInstanceNote(dbServerName(db)) },
  ]
}

/** The license as the Review step shows it: as entered. It is not a secret. */
function licenseRows(payload: InfrastructurePayload): ReviewRowData[] {
  const license = payload.license as Record<string, string> | undefined
  if (!license) return []
  return licenseFieldsFor(environmentTypeOf(payload.location.environment)).map((field) => ({
    label: field.label,
    value: license[field.key] ?? '',
    mono: true,
  }))
}

function addOnRows(addOn: AddOnPayload | null): ReviewRowData[] {
  if (!addOn) return [{ label: 'Used', value: yes(false) }]
  return [
    { label: 'Used', value: yes(true) },
    { label: 'Golden image', value: addOn.goldenImage.name },
    { label: 'Network', value: networkText(addOn.network), mono: true },
  ]
}

function iisRows(addOn: IisAddOnPayload | undefined): ReviewRowData[] {
  if (!addOn) return [{ label: 'Used', value: yes(false) }]
  const server = addOn.iisServer
  return [
    { label: 'Used', value: yes(true) },
    { label: 'IIS server', value: server.name, mono: true },
    { label: 'Resource group', value: server.resourceGroup, mono: true },
    { label: 'Image', value: server.image },
    { label: 'Network', value: `${server.vnet} / ${server.subnet}`, mono: true },
    { label: 'IP address', value: server.ip, mono: true },
  ]
}

/**
 * The sections of the Review step, in order. Every selection appears, grouped, with where to edit it. `vmSizes` (the product lines'
 * new servers) say which VM size is built: the catalog's, or the nearest series of the same size where the region does not offer it.
 */
export function summariseInfrastructure(payload: InfrastructurePayload, vmSizes?: readonly VmSizeCheck[]): ReviewSectionData[] {
  // The questions only the product lines are asked: their presence in the payload is what says this one is a line.
  const lineSections: ReviewSectionData[] = payload.linkVersion
    ? [
        { title: 'Customer Portal', editStep: 'addons', rows: iisRows(payload.customerPortal) },
        ...componentsFor(payload.product).map((component): ReviewSectionData => ({ title: component.name, editStep: 'addons', rows: iisRows(payload[component.key]) })),
        { title: 'Link', editStep: 'addons', rows: [{ label: 'Link', value: linkVersionLabel(payload.linkVersion) }] },
      ]
    : []
  // Advanced Analytics and Bartender are asked of Process Pro Global only: their sections show when the payload has them.
  const analyticsSections: ReviewSectionData[] =
    'advancedAnalytics' in payload
      ? [
          { title: 'Advanced Analytics', editStep: 'addons', rows: addOnRows(payload.advancedAnalytics ?? null) },
          { title: 'Bartender', editStep: 'addons', rows: addOnRows(payload.bartender ?? null) },
        ]
      : []
  const license = licenseRows(payload)
  return [
    { title: 'Product', editStep: null, rows: [{ label: 'Product', value: payload.product.name }] },
    {
      title: 'Location',
      editStep: 'location',
      rows: [
        { label: 'Environment', value: payload.location.environment.label },
        { label: 'Region', value: payload.location.region.label },
        { label: 'Resource group', value: payload.location.resourceGroup.name, mono: true },
        ...(payload.customerNumber !== null ? [{ label: CUSTOMER_CODE_LABEL, value: String(payload.customerNumber), mono: true }] : []),
      ],
    },
    // The license is asked on the Application (ERP) step: Edit goes there.
    ...(license.length > 0 ? [{ title: 'License', editStep: 'application' as const, rows: license }] : []),
    { title: 'Level', editStep: 'level', rows: [{ label: 'Level', value: payload.level === 'appCentral' ? 'AppCentral level' : 'Domain level' }] },
    {
      title: 'Servers',
      editStep: 'servers',
      rows: [
        ...rdsRows(payload.rdsServer, sizeOfTier(vmSizes, 'rds')),
        ...dbRows(payload.dbServer, sizeOfTier(vmSizes, 'db')),
        ...sqlInstanceRows(payload.sqlInstance, payload.dbServer),
      ],
    },
    ...analyticsSections,
    ...lineSections,
  ]
}
